import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Textarea } from './ui/textarea';
import { useAuth } from '../Services/AuthContext';
import { apiFetch } from "../utils/api";
import { toast } from 'sonner';

import {
  Users,
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  UserPlus,
  UserCheck,
  UserX,
  Calendar,
  Crown,
  Award,
  Star,
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  Download,
  User2,
  Building2,
  Mail,
  MailPlus,
  Phone,
  XCircle,
  MessageSquare,
  FileText,
  X
} from 'lucide-react';
import { toUpper } from '../utils/formatUtils';

export function UserManagement() {

  const { idToken, currentPartner } = useAuth();
  const [userRequests, setUserRequests] = useState([]);
  const [filteredUserRequests, setFilteredUserRequests] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [userRequestsSearchTerm, setUserRequestsSearchTerm] = useState('');
  const [userRequestsFilterStatus, setUserRequestsFilterStatus] = useState('all');
  const [userRequestsFilterRole, setUserRequestsFilterRole] = useState('all');
  const [userRequestsFilterPartner, setUserRequestsFilterPartner] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [isViewRequestOpen, setIsViewRequestOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState<'approve' | 'decline' | 'info'>('info');
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterPartner, setFilterPartner] = useState('all');
  const [uniquePartners, setUniquePartners] = useState([]);
  const [roles, setRoles] = useState([]);
  const [filterTier, setFilterTier] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('users');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isViewUserOpen, setIsViewUserOpen] = useState(false);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    partners: [],
    tier: 'bronze',
    phone: '',
    status: 'active'
  });

  // Helper functions for User Requests
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-worldsview-orange';
      case 'approved': return 'bg-worldsview-green';
      case 'declined': return 'bg-worldsview-red';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return Clock;
      case 'approved': return CheckCircle;
      case 'declined': return XCircle;
      default: return AlertTriangle;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Dealer Principal': return 'bg-worldsview-purple';
      case 'Sales Manager': return 'bg-worldsview-blue';
      case 'Salesperson': return 'bg-worldsview-green';
      case 'Customer Success': return 'bg-worldsview-blue';
      case 'Finance': return 'bg-worldsview-orange';
      case 'Technical': return 'bg-worldsview-green';
      case 'Marketing': return 'bg-worldsview-purple';
      default: return 'bg-gray-500';
    }
  };


  const getUserRoleColor = (role: string) => {
    switch (role) {
      case 'system-admin': return 'bg-worldsview-purple';
      case 'dealer-principal': return 'bg-worldsview-purple';
      case 'sales-manager': return 'bg-worldsview-blue';
      case 'salesperson': return 'bg-worldsview-green';
      case 'customer-success': return 'bg-worldsview-blue';
      case 'finance': return 'bg-worldsview-orange';
      case 'technical': return 'bg-worldsview-green';
      case 'marketing': return 'bg-worldsview-purple';
      default: return 'bg-gray-500';
    }
  };

  const getUserStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return UserX;
      case 'pending': return Clock;
      default: return AlertTriangle;
    }
  };

  const getUserStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-worldsview-green';
      case 'inactive': return 'bg-worldsview-red';
      case 'pending': return 'bg-worldsview-orange';
      default: return 'bg-gray-500';
    }
  };




  const uniqueRequestPartners = [...new Set(userRequests.map(r => r.partnerOrganization))].sort();

  // Event handlers
  const handleViewUser = (user: any) => {
    setSelectedUser(user);
    setIsViewUserOpen(true);
  };

  const handleReinviteUser = async (user: any) => {
    const tk = await idToken();
    try {
      await apiFetch(tk, `/api/users/${user.id}/reinvite`, { method: "POST" });
      toast.success(`Invitation resent to ${user.email}`);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to resend invitation. Please try again.');
    }
  };

  const handleEditUser = (user: any) => {
    setEditUser({ ...user });
    setIsEditUserOpen(true);
  };

  const handleCreateUser = async () => {
    console.log('Creating user:', newUser);
    let tk = await idToken();
    const res = apiFetch(tk, "/api/users", {
      method: "POST",
      body: JSON.stringify(newUser)
    });
    setIsCreateUserOpen(false);
    setNewUser({
      first_name: '',
      last_name: '',
      email: '',
      role: '',
      partners: [],
      tier: 'bronze',
      phone: '',
      status: 'active'
    });
    fetchUsers();
  };

  const handleSaveEditUser = async () => {
    console.log('Saving user changes:', editUser);
    const res = await apiFetch(await idToken(), "/api/users", {
      method: "PATCH",
      body: JSON.stringify(editUser)
    });
    setUsers(res.data)
    setFilteredUsers(res.data)
    setSearchTerm('');
    setIsEditUserOpen(false);
    setEditUser(null);
    fetchUsers();
  };

  const handleExportUsers = () => {
    console.log('Exporting users...');
    const csvContent = "data:text/csv;charset=utf-8," +
      "Name,Email,Role,Partner,Phone,Status,Last Login\n" +
      filteredUsers.map(u => {
        const partnerNames = u.partners && Array.isArray(u.partners) 
                             ? u.partners.map(p => p.name).join(', ') 
                             : u.partner || '';
        const roleName = u.role && u.role.name ? u.role.name : '';
        return `"${u.first_name || ''} ${u.last_name || ''}","${u.email || ''}","${roleName}","${partnerNames}","${u.phone || ''}","${u.status || ''}","${u.last_login || ''}"`;
      }).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "users_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // User Requests handlers
  const handleViewRequest = (request: any) => {
    setSelectedRequest(request);
    setIsViewRequestOpen(true);
  };

  const handleApproveRequest = (requestId: number) => {
    const request = userRequests.find(r => r.id === requestId);
    setSelectedRequest(request);
    setFeedbackType('approve');
    setIsFeedbackOpen(true);
  };

  const handleDeclineRequest = (requestId: number) => {
    const request = userRequests.find(r => r.id === requestId);
    setSelectedRequest(request);
    setFeedbackType('decline');
    setIsFeedbackOpen(true);
  };

  const handleSendFeedback = async () => {
    console.log('Sending feedback:', feedbackText, 'Type:', feedbackType);

    let tk = await idToken();
    const res = apiFetch(tk, `/api/user_creation_requests`, {
      method: "PATCH",
      body: JSON.stringify({ request: selectedRequest, feedbackText: feedbackText, feedbackType: feedbackType, partner_id: currentPartner.partner_id })
    });

    setIsFeedbackOpen(false);
    setFeedbackText('');
    setFeedbackType('info');
    fetchUserRequests();
    fetchUsers();
  };

  const handleExportUserRequests = () => {
    console.log('Exporting user requests...');
    const csvContent = "data:text/csv;charset=utf-8," +
      "Request Date,Requested By,Partner Organization,New User Name,Email,Role,Status,Justification\n" +
      filteredUserRequests.map(r =>
        `"${new Date(r.created_at || '').toLocaleDateString()}","${r.requestor.first_name || ''} ${r.requestor.last_name || ''}","${r.partner.name || ''}","${r.first_name || ''} ${r.last_name || ''}","${r.email || ''}","${r.role || ''}","${r.status || ''}","${r.justification || ''}"`
      ).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "user_requests_export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRequestCounts = () => {
    return {
      all: userRequests.length,
      pending: userRequests.filter(r => r.status === 'pending').length,
      approved: userRequests.filter(r => r.status === 'approved').length,
      declined: userRequests.filter(r => r.status === 'declined').length
    };
  };

  const requestCounts = getRequestCounts();

  const fetchUserRequests = async () => {
    let tk = await idToken();
    let queryParams = new URLSearchParams();
    queryParams.append('partner_id', currentPartner.partner_id);

    if (userRequestsSearchTerm) {
      queryParams.append('search_term', userRequestsSearchTerm);
    }
    if (userRequestsFilterStatus !== 'all') {
      queryParams.append('status', userRequestsFilterStatus);
    }
    if (userRequestsFilterRole !== 'all') {
      queryParams.append('role', userRequestsFilterRole);
    }
    if (userRequestsFilterPartner !== 'all') {
      queryParams.append('partner_id_filter', userRequestsFilterPartner);
    }

    const res = apiFetch(tk, `/api/user_creation_requests?${queryParams.toString()}`);
    res.then(async (response) => {
      setUserRequests(response.data);
      setFilteredUserRequests(response.data);
    });
  };

  const fetchUsers = async () => {
    let tk = await idToken();
    const res = apiFetch(tk, "/api/users");
    res.then(async (users_response) => {
      setUsers(users_response.data);
      setFilteredUsers(users_response.data)
    });
  };

  useEffect(() => {
    const fetchPartners = async () => {
      let tk = await idToken();
      const res = apiFetch(tk, "/api/partners");
      res.then(async (partners_response) => {
        setUniquePartners(partners_response.data);
      });
    };

    const fetchRoles = async () => {
      let tk = await idToken();
      const res = apiFetch(tk, "/api/users/roles");
      res.then(async (roles_response) => {
        setRoles(roles_response.data);
      });
    };

    fetchUserRequests();
    fetchPartners();
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUserRequests();
  }, [userRequestsSearchTerm, userRequestsFilterStatus, userRequestsFilterRole, userRequestsFilterPartner]);

  useEffect(() => {
    let data = users.filter(user => {
      const matchesSearch = user?.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user?.partner?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role.name === filterRole;
      const matchesPartner = filterPartner === 'all' || user.partner === filterPartner;
      const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
      return matchesSearch && matchesRole && matchesPartner && matchesStatus;
    });
    setFilteredUsers(data);
  }, [searchTerm, filterRole, filterStatus, users])

  return (
    <div className="space-y-6 animate-fade-in-fast">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1>User Management</h1>
            <p className="text-muted-foreground mt-2">
              Manage user accounts, access requests, and activity monitoring
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-worldsview-blue" />
            <Badge className="bg-worldsview-blue text-white">
              {filteredUsers.length} Active Users
            </Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="glass-card">
          <CardContent className="pt-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="users">Users ({filteredUsers.length})</TabsTrigger>
              <TabsTrigger value="user-requests">
                User Requests ({requestCounts.pending} pending)
              </TabsTrigger>
            </TabsList>
          </CardContent>
        </div>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-worldsview-blue" />
                  User Accounts
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" className="glass-button" onClick={handleExportUsers}>
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                  <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-worldsview-blue hover:bg-worldsview-blue/90">
                        <Plus className="w-4 h-4 mr-2" />
                        Add User
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl glass-modal">
                      <DialogHeader>
                        <DialogTitle>Add New User</DialogTitle>
                        <DialogDescription>
                          Create a new user account in the system.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="first-name">First Name *</Label>
                          <Input
                            id="first-name"
                            value={newUser.first_name}
                            onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                            placeholder="Enter first name"
                            className="glass-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last-name">Last Name *</Label>
                          <Input
                            id="last-name"
                            value={newUser.last_name}
                            onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                            placeholder="Enter last name"
                            className="glass-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user-email">Email Address *</Label>
                          <Input
                            id="user-email"
                            type="email"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            placeholder="Enter email address"
                            className="glass-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user-role">Role</Label>
                          <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                            <SelectTrigger className="glass-input">
                              <SelectValue placeholder="Select role" />
                            </SelectTrigger>
                            <SelectContent>
                              {roles.map(role => (
                                <SelectItem value={role.name}>{role.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2 col-span-2">
                          <Label htmlFor="user-partner">Partner Organizations</Label>
                          <div className="glass-input p-3 rounded-md space-y-2 max-h-48 overflow-y-auto">
                            {uniquePartners.map(partner => (
                              <div key={partner.id} className="flex items-center space-x-2">
                                <Checkbox
                                  id={`new-partner-${partner.id}`}
                                  checked={newUser?.partners?.includes(partner)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setNewUser({ ...newUser, partners: [...newUser.partners, partner] });
                                    } else {
                                      setNewUser({ ...newUser, partners: newUser.partners.filter(p => p.id !== partner.id) });
                                    }
                                  }}
                                />
                                <label
                                  htmlFor={`new-partner-${partner.id}`}
                                  className="text-sm cursor-pointer flex-1"
                                >
                                  {partner.name} - {partner.company_id}
                                </label>
                              </div>
                            ))}
                          </div>
                          {newUser?.partners?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {newUser.partners.map((partner, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs flex items-center gap-1">
                                  {partner.name} - {partner.company_id}
                                  <X
                                    className="w-3 h-3 cursor-pointer"
                                    onClick={() => setNewUser({ ...newUser, partners: newUser.partners.filter(p => p !== partner) })}
                                  />
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="user-phone">Phone Number</Label>
                          <Input
                            id="user-phone"
                            value={newUser.phone}
                            onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                            placeholder="Enter phone number"
                            className="glass-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-4">
                        <Button variant="outline" onClick={() => setIsCreateUserOpen(false)} className="glass-button">
                          Cancel
                        </Button>
                        <Button onClick={handleCreateUser} className="bg-worldsview-blue hover:bg-worldsview-blue/90">
                          Create User
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Filters */}
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search users by name, email, or partner..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 glass-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={filterRole} onValueChange={setFilterRole}>
                    <SelectTrigger className="w-36 glass-input">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={role.name} value={role.name}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-32 glass-input">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="disabled">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Users Table */}
              <div className="rounded-md border glass-input">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Partner Organization</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => {
                        const StatusIcon = getUserStatusIcon(user.status);

                        // const isCurrentUser = user && currentUser.id === user.id;
                        const isCurrentUser = false;

                        return (
                          <TableRow key={`${user.id}${user?.partner?.name}`} className={`hover:bg-muted/50 ${isCurrentUser ? 'bg-worldsview-blue/5' : ''}`}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-worldsview-blue text-white flex items-center justify-center text-sm font-medium">
                                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                                </div>
                                <div>
                                  <div className="font-medium flex items-center gap-2">
                                    {user?.first_name} {user?.last_name}
                                    {isCurrentUser && (
                                      <Badge variant="outline" className="text-xs">You</Badge>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground">{user.email}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                {user.partners ? (
                                  <div className="flex flex-wrap gap-1">
                                    {user.partners.map((partner, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        {partner.name} - {toUpper(partner.company_id)}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="font-medium text-sm">{user.partner}</div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-muted-foreground">
                                {user?.role?.name}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-white flex items-center gap-1 w-fit ${getUserStatusColor(user.status)}`}>
                                <StatusIcon className="w-3 h-3" />
                                {user?.status?.charAt(0)?.toUpperCase() + user?.status?.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="glass-button"
                                  onClick={() => handleViewUser(user)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="glass-button"
                                  title="Reinvite to Entra"
                                  onClick={() => handleReinviteUser(user)}
                                >
                                  <MailPlus className="w-4 h-4" />
                                </Button>
                                {/* <Button
                                  variant="ghost"
                                  size="sm"
                                  className="glass-button"
                                  onClick={() => handleEditUser(user)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button> */}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No users found matching your search criteria.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Requests Tab */}
        <TabsContent value="user-requests" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-worldsview-blue" />
                  User Access Requests
                </CardTitle>
                <Button variant="outline" className="glass-button" onClick={handleExportUserRequests}>
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search and Filters */}
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search requests by user name, email, or justification..."
                    value={userRequestsSearchTerm}
                    onChange={(e) => setUserRequestsSearchTerm(e.target.value)}
                    className="pl-10 glass-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Select value={userRequestsFilterStatus} onValueChange={setUserRequestsFilterStatus}>
                    <SelectTrigger className="w-36 glass-input">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={userRequestsFilterRole} onValueChange={setUserRequestsFilterRole}>
                    <SelectTrigger className="w-36 glass-input">
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {roles.map(role => (
                        <SelectItem key={`_r_${role.id}`} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={userRequestsFilterPartner} onValueChange={setUserRequestsFilterPartner}>
                    <SelectTrigger className="w-48 glass-input">
                      <SelectValue placeholder="All Partners" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Partners</SelectItem>
                      {uniquePartners.map(partner => (
                        <SelectItem key={`_o_${partner.id}`} value={partner.partner_id}>{partner.name} - {toUpper(partner.company_id)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Requests Table */}
              <div className="rounded-md border glass-input">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request Details</TableHead>
                      <TableHead>Requested User</TableHead>
                      <TableHead>Partner Organization</TableHead>
                      <TableHead>Role & Access</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUserRequests.length > 0 ? (
                      filteredUserRequests.map((request) => {
                        const StatusIcon = getStatusIcon(request.status);
                        return (
                          <TableRow key={request.id} className="hover:bg-muted/50">
                            <TableCell>
                              <div>
                                <div className="font-medium">Request #{request.id}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {request.created_at}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  By: {request.requestor.first_name} {request.requestor.last_name}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">{request.first_name} {request.last_name}</div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {request.email}
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {request.phone}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-worldsview-blue" />
                                <span className="font-medium">{request.partner.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="space-y-1">
                                <span className="text-sm text-muted-foreground">
                                  {request.role}
                                </span>

                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-white flex items-center gap-1 w-fit ${getStatusColor(request.status)}`}>
                                <StatusIcon className="w-3 h-3" />
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="glass-button"
                                  onClick={() => handleViewRequest(request)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {request.status === 'pending' && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="glass-button text-worldsview-green hover:bg-worldsview-green/10"
                                      onClick={() => handleApproveRequest(request.id)}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="glass-button text-worldsview-red hover:bg-worldsview-red/10"
                                      onClick={() => handleDeclineRequest(request.id)}
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No user requests found matching your search criteria.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* View User Dialog */}
      <Dialog open={isViewUserOpen} onOpenChange={setIsViewUserOpen}>
        <DialogContent className="max-w-2xl glass-modal">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>
              View detailed information about this user account.
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-worldsview-blue text-white flex items-center justify-center text-xl font-bold">
                  {selectedUser.first_name.charAt(0)}{selectedUser.last_name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedUser.first_name} {selectedUser.last_name}</h3>
                  <p className="text-muted-foreground">{selectedUser.email}</p>
                  <Badge className={`text-white ${getUserRoleColor(selectedUser.role.name)} mt-1`}>
                    {selectedUser.role.name}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Partner Organization{selectedUser.partners && selectedUser.partners.length > 1 ? 's' : ''}</Label>
                  {selectedUser.partners ? (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedUser.partners.map((partner: string, idx: number) => (
                        <Badge key={idx} variant="outline">
                          {partner.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">{selectedUser.partner}</p>
                  )}
                </div>

                <div>
                  <Label>Phone</Label>
                  <p className="font-medium">{selectedUser.phone}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className={`text-white ${getUserStatusColor(selectedUser.status)}`}>
                    {selectedUser.status.charAt(0).toUpperCase() + selectedUser.status.slice(1)}
                  </Badge>
                </div>
                <div>
                  <Label>Last Login</Label>
                  <p className="font-medium">
                    {/* {selectedUser.lastLogin} */}
                    {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsViewUserOpen(false)} className="glass-button">
              Close
            </Button>
            <Button onClick={() => {
              setIsViewUserOpen(false);
              handleEditUser(selectedUser);
            }} className="bg-worldsview-blue hover:bg-worldsview-blue/90">
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
        <DialogContent className="max-w-2xl glass-modal">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update the information for this user account.
            </DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">First Name *</Label>
                <Input
                  id="edit-first-name"
                  value={editUser.first_name}
                  onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })}
                  className="glass-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-last-name">Last Name *</Label>
                <Input
                  id="edit-name"
                  value={editUser.last_name}
                  onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })}
                  className="glass-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email Address *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editUser.email}
                  onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  className="glass-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select value={editUser.role.id} onValueChange={(value) => {
                  setEditUser({
                    ...editUser,
                    role: {
                      ...editUser.role,
                      id: value
                    }
                  });
                }}>

                  <SelectTrigger className="glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={`_edit_${role.id}`} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="edit-partner">Partner Organizations</Label>
                <div className="glass-input p-3 rounded-md space-y-2 max-h-48 overflow-y-auto">
                  {uniquePartners.map(partner => {
                    const userPartners = editUser.partners || [editUser.partner || ''];
                    return (
                      <div key={partner.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-partner-${partner.id}`}
                          checked={userPartners.includes(partner)}
                          onCheckedChange={(checked) => {
                            const currentPartners = editUser.partners || [editUser.partner || ''];
                            if (checked) {
                              setEditUser({ ...editUser, partners: [...currentPartners.filter(p => p), partner], partner: undefined });
                            } else {
                              const newPartners = currentPartners.filter(p => p !== partner);
                              setEditUser({ ...editUser, partners: newPartners, partner: undefined });
                            }
                          }}
                        />
                        <label
                          htmlFor={`edit-partner-${partner.id}`}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {partner.name} - {partner.company_id}
                        </label>
                      </div>
                    );
                  })}
                </div>
                {((editUser.partners && editUser.partners.length > 0) || editUser.partner) && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(editUser.partners || [editUser.partner]).filter(Boolean).map((partner, idx: number) => (
                      <Badge
                        onClick={() => {
                          const currentPartners = editUser.partners || [editUser.partner || ''];
                          const newPartners = currentPartners.filter((p) => ((p.name !== partner.name) && (p.company_id !== partner.company_id)));
                          setEditUser({ ...editUser, partners: newPartners, partner: undefined });
                        }}
                        key={idx} variant="secondary" className="text-xs flex items-center gap-1">
                        {partner.name} - {partner.company_id}
                        <X className="w-3 h-3 cursor-pointer" />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone Number</Label>
                <Input
                  id="edit-phone"
                  value={editUser.phone}
                  onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })}
                  className="glass-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select value={editUser.status} onValueChange={(value) => setEditUser({ ...editUser, status: value })}>
                  <SelectTrigger className="glass-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsEditUserOpen(false)} className="glass-button">
              Cancel
            </Button>
            <Button onClick={handleSaveEditUser} className="bg-worldsview-green hover:bg-worldsview-green/90">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Request Dialog */}
      <Dialog open={isViewRequestOpen} onOpenChange={setIsViewRequestOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto glass-modal">
          <DialogHeader>
            <DialogTitle>User Request Details</DialogTitle>
            <DialogDescription>
              Review detailed information about this user access request.
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              {/* Request Overview */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="glass-input gap-2">
                  <CardHeader>
                    <CardTitle className="text-base">Request Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <Label className="text-xs text-muted-foreground">Request ID</Label>
                      <p className="font-medium">#{selectedRequest.id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Request Date</Label>
                      <p className="font-medium">{selectedRequest.requestDate}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <Badge className={`text-white ${getStatusColor(selectedRequest.status)}`}>
                        {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass-input gap-2">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Requested By</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Name</Label>
                      <p className="font-medium">{selectedRequest.requestor.first_name} {selectedRequest.requestor.last_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <p className="font-medium">{selectedRequest.requestor.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Role</Label>
                      <p className="font-medium">{selectedRequest.requestor.roles[0].name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Organization</Label>
                      <p className="font-medium">{selectedRequest.partner.name}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* New User Details */}
              <Card className="glass-input gap-2">
                <CardHeader className="">
                  <CardTitle className="text-base">New User Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">First Name</Label>
                      <p className="font-medium">{selectedRequest.first_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Last Name</Label>
                      <p className="font-medium">{selectedRequest.last_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Email Address</Label>
                      <p className="font-medium">{selectedRequest.email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Phone Number</Label>
                      <p className="font-medium">{selectedRequest.phone}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Requested Role</Label>
                      <Badge className={`text-white ${getRoleColor(selectedRequest.role)}`}>
                        {selectedRequest.role}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Justification */}
              <Card className="glass-input gap-2">
                <CardHeader className="">
                  <CardTitle className="text-base">Business Justification</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{selectedRequest.justification}</p>
                </CardContent>
              </Card>

              {/* Admin Notes */}
              {selectedRequest.adminNotes && (
                <Card className="glass-input gap-2">
                  <CardHeader>
                    <CardTitle className="text-base">Admin Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedRequest.adminNotes}</p>
                    {selectedRequest.approvedBy && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Approved by {selectedRequest.approvedBy} on {selectedRequest.approvedDate}
                      </div>
                    )}
                    {selectedRequest.declinedBy && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Declined by {selectedRequest.declinedBy} on {selectedRequest.declinedDate}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsViewRequestOpen(false)} className="glass-button">
              Close
            </Button>
            {selectedRequest?.status === 'pending' && (
              <>
                <Button
                  onClick={() => {
                    handleApproveRequest(selectedRequest.id);
                    setIsViewRequestOpen(false);
                  }}
                  className="bg-worldsview-green hover:bg-worldsview-green/90"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => {
                    handleDeclineRequest(selectedRequest.id);
                    setIsViewRequestOpen(false);
                  }}
                  className="bg-worldsview-red hover:bg-worldsview-red/90"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen}>
        <DialogContent className="max-w-2xl glass-modal">
          <DialogHeader>
            <DialogTitle>
              {feedbackType === 'approve' ? 'Approve Request' : feedbackType === 'decline' ? 'Decline Request' : 'Send Feedback'}
            </DialogTitle>
            <DialogDescription>
              {feedbackType === 'approve'
                ? 'Add notes for approving this user request (optional).'
                : feedbackType === 'decline'
                  ? 'Please provide a reason for declining this request.'
                  : 'Provide feedback or additional information about this user request.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="feedback-text">
                {feedbackType === 'approve' ? 'Approval Notes (Optional)' : feedbackType === 'decline' ? 'Decline Reason' : 'Feedback Message'}
              </Label>
              <Textarea
                id="feedback-text"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder={
                  feedbackType === 'approve'
                    ? 'Enter any notes for this approval...'
                    : feedbackType === 'decline'
                      ? 'Enter the reason for declining this request...'
                      : 'Enter your feedback or additional information...'
                }
                className="glass-input min-h-24"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => {
              setIsFeedbackOpen(false);
              setFeedbackText('');
              setFeedbackType('info');
              setSelectedRequest(null);
            }} className="glass-button">
              Cancel
            </Button>
            <Button
              onClick={handleSendFeedback}
              className={
                feedbackType === 'approve'
                  ? 'bg-worldsview-green hover:bg-worldsview-green/90'
                  : feedbackType === 'decline'
                    ? 'bg-worldsview-red hover:bg-worldsview-red/90'
                    : 'bg-worldsview-blue hover:bg-worldsview-blue/90'
              }
            >
              {feedbackType === 'approve' ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Request
                </>
              ) : feedbackType === 'decline' ? (
                <>
                  <XCircle className="w-4 h-4 mr-2" />
                  Decline Request
                </>
              ) : (
                <>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Feedback
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}